import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  GraduationCap, Users, BookOpen, Mail, Phone, 
  ChevronRight, Grid3X3, List, AlertTriangle,
  CheckCircle, XCircle, User, TrendingUp, Calendar,
  FileSpreadsheet, FileText, ExternalLink, Video, X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import DetalleEstudiante from './DetalleEstudiante';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AvanceEstudiantes = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clases, setClases] = useState([]);
  const [claseSeleccionada, setClaseSeleccionada] = useState(null);
  const [estudiantes, setEstudiantes] = useState([]);
  const [loadingEstudiantes, setLoadingEstudiantes] = useState(false);
  const [vistaGrid, setVistaGrid] = useState(true);
  
  // Para admin: lista de profesores
  const [profesores, setProfesores] = useState([]);
  const [profesorSeleccionado, setProfesorSeleccionado] = useState(null);
  
  // Exportación
  const [exportando, setExportando] = useState(false);

  // 🆕 Lista única de todos los estudiantes (vista principal simplificada).
  // Reemplaza el flujo previo Profesor→Clase→Estudiantes.
  const [todosEstudiantes, setTodosEstudiantes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [incluirInactivos, setIncluirInactivos] = useState(false);

  // Ficha de estudiante embebida (no navega — preserva videollamadas activas
  // en Sala Docente). Si está seteada, renderiza DetalleEstudiante como
  // overlay fullscreen sobre Avance.
  const [estudianteSeleccionado, setEstudianteSeleccionado] = useState(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('access_token') || localStorage.getItem('token');
        const resp = await axios.get(
          `${API}/archivos/estudiantes-disponibles?incluir_inactivos=${incluirInactivos}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const list = Array.isArray(resp.data) ? resp.data : [];
        setTodosEstudiantes(list);
      } catch (e) {
        console.error('Error cargando estudiantes:', e);
        toast.error('No pude cargar la lista de estudiantes');
      } finally {
        setLoading(false);
      }
    })();
  }, [user, incluirInactivos]);

  const loadProfesores = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/profesores-lista`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      setProfesores(response.data);
    } catch (error) {
      console.error('Error loading profesores:', error);
      toast.error('Error al cargar profesores');
    } finally {
      setLoading(false);
    }
  };

  const loadClases = async (profesorId = null) => {
    try {
      setLoading(true);
      let url = `${API}/profesor/mis-clases-activas`;
      if (profesorId) {
        url += `?profesor_id=${profesorId}`;
      }
      const response = await axios.get(url, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      
      // Si es admin y seleccionó un profesor, filtrar sus clases
      let clasesData = response.data;
      if (user?.rol === 'admin' && profesorId) {
        clasesData = response.data.filter(c => c.profesor_id === profesorId);
      }
      
      setClases(clasesData);
    } catch (error) {
      console.error('Error loading clases:', error);
      toast.error('Error al cargar clases');
    } finally {
      setLoading(false);
    }
  };

  const selectProfesor = (profesor) => {
    setProfesorSeleccionado(profesor);
    setClaseSeleccionada(null);
    setEstudiantes([]);
    loadClases(profesor.id);
  };

  const selectClase = async (clase) => {
    setClaseSeleccionada(clase);
    setLoadingEstudiantes(true);
    
    try {
      const response = await axios.get(`${API}/profesor/clase/${clase.id}/estudiantes`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      setEstudiantes(response.data);
    } catch (error) {
      console.error('Error loading estudiantes:', error);
      toast.error('Error al cargar estudiantes');
    } finally {
      setLoadingEstudiantes(false);
    }
  };

  // Abrir detalle del estudiante EMBEBIDO (sin navegar fuera de Avance).
  // Esto preserva el árbol React de SalaDocente — clave para que el iframe
  // de videollamada / reunión activa NO se desmonte cuando el profesor
  // entra a ver datos de un alumno durante una clase virtual.
  const openDetalleEstudiante = (estudiante) => {
    if (!claseSeleccionada) return;
    setEstudianteSeleccionado({
      estudianteId: estudiante.id,
      claseId: claseSeleccionada.id,
    });
  };
  
  // Exportar a Excel
  const exportarExcel = async () => {
    if (!claseSeleccionada) return;
    
    setExportando(true);
    try {
      const response = await axios.get(
        `${API}/profesor/clase/${claseSeleccionada.id}/exportar-avance`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }}
      );
      
      const data = response.data;
      
      // Crear CSV
      let csv = 'Nombre,Apellido,Email,Celular,Promedio,Asistencias,% Asistencia,Videos Vistos,Total Videos,Estado,Evaluaciones\n';
      data.estudiantes.forEach(est => {
        csv += `"${est.nombre}","${est.apellido}","${est.email}","${est.celular || ''}",${est.promedio},${est.asistencias}/${est.total_lecciones},${est.porcentaje_asistencia}%,${est.video_accesos || 0},${est.total_videos_clase || 0},"${est.estado}",${est.total_evaluaciones}\n`;
      });
      
      // Descargar archivo
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `avance_${claseSeleccionada.titulo.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Archivo exportado correctamente');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Error al exportar datos');
    } finally {
      setExportando(false);
    }
  };
  
  // Exportar a PDF (genera vista de impresión)
  const exportarPDF = async () => {
    if (!claseSeleccionada) return;
    
    setExportando(true);
    try {
      const response = await axios.get(
        `${API}/profesor/clase/${claseSeleccionada.id}/exportar-avance`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }}
      );
      
      const data = response.data;
      
      // Crear ventana de impresión
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Reporte de Avance - ${data.clase.titulo}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #1e40af; border-bottom: 2px solid #3b82f6; padding-bottom: 10px; }
            .header-info { display: flex; justify-content: space-between; margin-bottom: 20px; }
            .stat-box { background: #f3f4f6; padding: 15px; border-radius: 8px; text-align: center; }
            .stat-number { font-size: 24px; font-weight: bold; color: #1e40af; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { background: #3b82f6; color: white; padding: 10px; text-align: left; }
            td { padding: 10px; border-bottom: 1px solid #e5e7eb; }
            tr:nth-child(even) { background: #f9fafb; }
            .estado-excelente { color: #059669; font-weight: bold; }
            .estado-regular { color: #d97706; font-weight: bold; }
            .estado-riesgo { color: #dc2626; font-weight: bold; }
            .footer { margin-top: 30px; text-align: center; color: #6b7280; font-size: 12px; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <h1>📊 Reporte de Avance de Estudiantes</h1>
          <div class="header-info">
            <div>
              <p><strong>Clase:</strong> ${data.clase.titulo}</p>
              <p><strong>Profesor:</strong> ${data.clase.profesor || 'N/A'}</p>
              <p><strong>Fecha:</strong> ${new Date().toLocaleDateString('es-UY')}</p>
            </div>
            <div style="display: flex; gap: 15px;">
              <div class="stat-box">
                <div class="stat-number">${data.clase.total_estudiantes}</div>
                <div>Estudiantes</div>
              </div>
              <div class="stat-box">
                <div class="stat-number">${data.clase.promedio_general}%</div>
                <div>Promedio</div>
              </div>
            </div>
          </div>
          
          <div style="display: flex; gap: 15px; margin-bottom: 20px;">
            <div class="stat-box" style="flex: 1; background: #d1fae5;">
              <div class="stat-number" style="color: #059669;">${data.estadisticas.excelentes}</div>
              <div>Excelentes (≥80%)</div>
            </div>
            <div class="stat-box" style="flex: 1; background: #fef3c7;">
              <div class="stat-number" style="color: #d97706;">${data.estadisticas.regulares}</div>
              <div>Regulares (60-79%)</div>
            </div>
            <div class="stat-box" style="flex: 1; background: #fee2e2;">
              <div class="stat-number" style="color: #dc2626;">${data.estadisticas.en_riesgo}</div>
              <div>En Riesgo (<60%)</div>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Nombre Completo</th>
                <th>Email</th>
                <th>Promedio</th>
                <th>Asistencia</th>
                <th>Videos</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              ${data.estudiantes.map((est, idx) => `
                <tr>
                  <td>${idx + 1}</td>
                  <td>${est.nombre_completo}</td>
                  <td>${est.email}</td>
                  <td>${est.promedio}%</td>
                  <td>${est.asistencias}/${est.total_lecciones} (${est.porcentaje_asistencia}%)</td>
                  <td>${est.video_accesos || 0}</td>
                  <td class="estado-${est.estado === 'Excelente' ? 'excelente' : est.estado === 'Regular' ? 'regular' : 'riesgo'}">${est.estado}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="footer">
            <p>Instituto Bíblico Apostólico del Uruguay - Sistema de Gestión Académica</p>
            <p>Generado el ${new Date().toLocaleString('es-UY')}</p>
          </div>
          
          <script>window.onload = function() { window.print(); }</script>
        </body>
        </html>
      `);
      printWindow.document.close();
      
      toast.success('Reporte generado - se abrirá la ventana de impresión');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Error al generar reporte');
    } finally {
      setExportando(false);
    }
  };

  const enviarEmailAlerta = async (estudiante) => {
    try {
      // Usar el endpoint existente de calificaciones bajas
      await axios.post(`${API}/profesor/enviar-alerta-calificacion`, {
        estudiante_id: estudiante.id,
        clase_id: claseSeleccionada.id,
        promedio: estudiante.promedio_clase
      }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      toast.success(`Email de alerta enviado a ${estudiante.nombre_completo}`);
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Error al enviar email de alerta');
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'verde': return 'bg-green-500';
      case 'amarillo': return 'bg-yellow-500';
      case 'rojo': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getEstadoBadge = (estado) => {
    switch (estado) {
      case 'verde': return <Badge className="bg-green-100 text-green-800">Excelente</Badge>;
      case 'amarillo': return <Badge className="bg-yellow-100 text-yellow-800">Regular</Badge>;
      case 'rojo': return <Badge className="bg-red-100 text-red-800">En riesgo</Badge>;
      default: return null;
    }
  };

  // Header con info del profesor
  const renderProfesorHeader = () => {
    const profesor = user?.rol === 'admin' ? profesorSeleccionado : user;
    if (!profesor) return null;

    return (
      <div className="flex items-center gap-4 mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
        <div className="w-16 h-16 rounded-full overflow-hidden bg-blue-200 flex-shrink-0">
          {profesor.foto || profesor.foto_perfil ? (
            <img src={profesor.foto || profesor.foto_perfil} alt={profesor.nombre} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-blue-600">
              <User className="w-8 h-8" />
            </div>
          )}
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-800">
            {profesor.nombre_completo || `${profesor.nombre || ''} ${profesor.apellido || ''}`.trim()}
          </h2>
          <p className="text-gray-500">Profesor</p>
          {user?.rol === 'admin' && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => {
                setProfesorSeleccionado(null);
                setClaseSeleccionada(null);
                setEstudiantes([]);
              }}
              className="mt-1 text-blue-600"
            >
              ← Cambiar profesor
            </Button>
          )}
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════
  // 🆕 Vista principal: lista única de estudiantes.
  // Al elegir uno → renderea DetalleEstudiante embebido (sin navegar).
  // El botón "Volver" solo cierra la ficha (no sale del componente).
  // ═══════════════════════════════════════════════════════════════════
  if (estudianteSeleccionado) {
    return (
      <div className="space-y-4" data-testid="avance-ficha-estudiante">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <Button
            variant="ghost"
            onClick={() => setEstudianteSeleccionado(null)}
            data-testid="ficha-estudiante-volver-btn"
            className="text-gray-700 hover:text-gray-900"
          >
            <i className="fas fa-arrow-left mr-2" />
            Volver a la lista de estudiantes
          </Button>
        </div>
        <div data-testid="ficha-estudiante-inline">
          <DetalleEstudiante
            estudianteId={estudianteSeleccionado.estudianteId}
            claseId={estudianteSeleccionado.claseId || null}
            embedded
            onClose={() => setEstudianteSeleccionado(null)}
          />
        </div>
      </div>
    );
  }

  // Filtrar por buscador
  const q = (searchTerm || '').trim().toLowerCase();
  const listaFiltrada = q
    ? todosEstudiantes.filter((e) =>
        (e.nombre || '').toLowerCase().includes(q) ||
        (e.email || '').toLowerCase().includes(q))
    : todosEstudiantes;

  return (
    <div className="space-y-6" data-testid="avance-estudiantes-lista">
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
          <Users className="w-7 h-7 text-white" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-800">Avance de Estudiantes</h1>
          <p className="text-gray-500 text-sm">
            {loading ? 'Cargando…' : `${todosEstudiantes.length} estudiante${todosEstudiantes.length === 1 ? '' : 's'} disponibles`}
          </p>
        </div>
      </div>

      {/* Buscador + toggle activos */}
      <div className="flex flex-col sm:flex-row gap-2 bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
        <div className="flex items-center gap-2 flex-1">
          <i className="fas fa-search text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nombre o email…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 outline-none text-sm"
            data-testid="avance-search-input"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="text-gray-400 hover:text-gray-600" title="Limpiar">
              <i className="fas fa-times-circle" />
            </button>
          )}
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none" data-testid="toggle-incluir-inactivos">
          <input
            type="checkbox"
            checked={incluirInactivos}
            onChange={(e) => setIncluirInactivos(e.target.checked)}
            className="w-4 h-4 accent-blue-600"
          />
          <span>Incluir inactivos</span>
        </label>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      ) : listaFiltrada.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            <Users className="w-12 h-12 mx-auto text-gray-300 mb-2" />
            <p>{q ? 'Sin resultados para tu búsqueda.' : 'No hay estudiantes disponibles.'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {listaFiltrada.map((est) => (
            <Card
              key={est.id}
              className="cursor-pointer hover:shadow-md hover:border-blue-300 transition"
              onClick={() => setEstudianteSeleccionado({ estudianteId: est.id, claseId: null })}
              data-testid={`avance-estudiante-card-${est.id}`}
            >
              <CardContent className="p-3">
                <div className="flex items-center gap-3">
                  {est.foto_perfil ? (
                    <img
                      src={est.foto_perfil}
                      alt={est.nombre || est.email}
                      className="w-11 h-11 rounded-full object-cover flex-shrink-0 border border-gray-200"
                      onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }}
                    />
                  ) : null}
                  <div
                    className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 items-center justify-center text-white font-semibold flex-shrink-0"
                    style={{ display: est.foto_perfil ? 'none' : 'flex' }}
                  >
                    {(est.nombre || est.email || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-900 truncate">{est.nombre || '—'}</p>
                      {est.activo === false && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 font-medium flex-shrink-0">
                          Inactivo
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate">{est.email}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AvanceEstudiantes;
